import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Tag } from 'lucide-react';
import RelatedTools from '../components/RelatedTools';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { useLang } from '../context/LanguageContext';
import {
  fetchWithRetry,
  readCache,
  writeCache,
  isCacheFresh,
} from '../lib/fetchWithRetry';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_IMAGES = {
  'Astrology': '/media/stock/7059700f_photo-1701014159143-09482059f571.jpg',
  'Puja': '/media/stock/6dde88e6_pexels-photo-12936912.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  'Career': '/media/stock/1b9d2af9_photo-1758876019673-704b039d405c.jpg',
  'Gemstone': '/media/stock/3777e05d_pexels-photo-6806707.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  'Vastu': '/media/stock/7e6d1694_pexels-photo-9976121.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  'General': '/media/stock/5b0bfe56_photo-1639548206755-b20081a7cadc.jpg',
  'Kundli': '/media/stock/72a177ba_pexels-photo-7222053.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  'Horoscope': '/media/stock/6d0ac1ca_photo-1708257105952-ff45589268a5.jpg',
};

const FALLBACK_IMAGES = [
  '/media/stock/7059700f_photo-1701014159143-09482059f571.jpg',
  '/media/stock/5b0bfe56_photo-1639548206755-b20081a7cadc.jpg',
  '/media/stock/6d0ac1ca_photo-1708257105952-ff45589268a5.jpg',
  '/media/stock/6dde88e6_pexels-photo-12936912.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  '/media/stock/88c7808a_photo-1698227118172-7ab5614f889a.jpg',
  '/media/stock/72a177ba_pexels-photo-7222053.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
];

const getBlogImage = (blog, index) => {
  if (blog.image_url) return blog.image_url;
  if (blog.category && CATEGORY_IMAGES[blog.category]) return CATEGORY_IMAGES[blog.category];
  return FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
};

// Detect blog language from content
const detectBlogLang = (blog) => {
  const text = (blog.title || '') + (blog.content || '');
  const hindiChars = (text.match(/[\u0900-\u097F]/g) || []).length;
  const totalChars = text.replace(/\s/g, '').length || 1;
  return hindiChars / totalChars > 0.3 ? 'hi' : 'en';
};

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [langFilter, setLangFilter] = useState('all');
  const [fetchError, setFetchError] = useState('');
  const [servedFromCache, setServedFromCache] = useState(false);
  const { lang, t } = useLang();
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogs();
  }, []);

  // Auto-translate blogs based on selected language
  useEffect(() => {
    if (blogs.length > 0) {
      blogs.forEach(blog => {
        const blogLang = detectBlogLang(blog);
        // Hindi blog + English selected → translate to English
        // English blog + Hindi selected → translate to Hindi
        if (blogLang !== lang && !translations[`${blog.id}_${lang}`] && !translating[blog.id]) {
          translateBlog(blog.id, lang);
        }
      });
    }
  }, [lang, blogs]);

  const translateBlog = async (blogId, targetLang) => {
    setTranslating(prev => ({ ...prev, [blogId]: true }));
    try {
      const res = await axios.post(`${API}/api/blogs/translate`, { blog_id: blogId, target_lang: targetLang });
      setTranslations(prev => ({ ...prev, [`${blogId}_${targetLang}`]: res.data }));
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setTranslating(prev => ({ ...prev, [blogId]: false }));
    }
  };

  const getBlogTitle = (blog) => {
    const blogLang = detectBlogLang(blog);
    if (blogLang !== lang && translations[`${blog.id}_${lang}`]?.title) return translations[`${blog.id}_${lang}`].title;
    return blog.title;
  };
  
  const getBlogExcerpt = (blog) => {
    const blogLang = detectBlogLang(blog);
    if (blogLang !== lang && translations[`${blog.id}_${lang}`]?.excerpt) return translations[`${blog.id}_${lang}`].excerpt;
    return blog.excerpt;
  };

  const fetchBlogs = async () => {
    // Feb 24, 2026 — resilient fetch with 3× exponential-backoff retry +
    // localStorage last-success cache. Fixes the intermittent
    // "कोई ब्लॉग नहीं मिला" false negative that appeared during deploy /
    // mobile network hiccups.
    setFetchError('');
    setServedFromCache(false);
    // Instant optimistic paint: if we have a cached list, show it right
    // away while re-fetching in the background. Users NEVER see an empty
    // list unless they genuinely have no cache and the network is dead.
    const cached = readCache('blog-list');
    if (cached && Array.isArray(cached.data) && cached.data.length) {
      setBlogs(cached.data);
      setLoading(false);
      setServedFromCache(true);
    }
    try {
      const response = await fetchWithRetry(
        () => axios.get(`${API}/api/blogs`, { timeout: 15000 }),
        { attempts: 3, baseDelayMs: 500 }
      );
      if (Array.isArray(response.data)) {
        setBlogs(response.data);
        writeCache('blog-list', response.data);
        setServedFromCache(false);
      }
    } catch (error) {
      console.error('Error fetching blogs after retries:', error);
      // If we served stale cache, keep showing it silently — the user
      // never saw a broken state. Only surface the error UI when there
      // is nothing at all to display.
      if (!(cached && isCacheFresh(cached))) {
        setFetchError(lang === 'hi'
          ? 'कनेक्शन में दिक्कत — कुछ ही क्षण में पुनः प्रयास करें।'
          : 'Connection issue — please retry in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const filteredBlogs = blogs;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Astrology Blog | ज्योतिष ब्लॉग"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'Blog', path: '/blog' },
        ]}
        description="वैदिक ज्योतिष, कुंडली, राशिफल, वास्तु, अंकशास्त्र, नक्षत्र, ग्रह उपाय और मंगल दोष पर authoritative articles। Read in-depth Vedic astrology blogs by Vivek Mudgal Astrologer (33+ years experience)."
        path="/blog"
        keywords="astrology blog, ज्योतिष ब्लॉग, vedic astrology, kundli, rashifal, vastu, numerology, mangal dosha, Vivek Mudgal"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'ViratAstro Astrology Blog',
          description: 'Vedic Astrology articles by Vivek Mudgal',
          url: 'https://viratastro.cloud/blog',
          author: { '@type': 'Person', name: 'Vivek Mudgal' },
        }}
      />
      <Header />
      <FloatingActions />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 cosmic-bg">
        <div className="container-custom text-center">
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body">
            {t('blog.tag')}
          </span>
          <h1 className="font-heading text-5xl md:text-6xl font-medium text-text-main mt-4 mb-6">
            {t('blog.title')} <span className="text-gold-gradient">{lang === 'hi' ? '' : 'Blog'}</span>
          </h1>
          <p className="text-text-muted max-w-2xl mx-auto">
            {lang === 'hi' 
              ? 'राशिफल, कुंडली, वास्तु और ज्योतिष के लेख पढ़ें। जीवन सुधारने के उपाय।'
              : 'Read articles about horoscope, kundli, vastu and astrology. Tips to improve your life.'}
          </p>
          {/* Language Filter */}
          <div className="flex items-center justify-center gap-2 mt-6" data-testid="blog-lang-filter">
            {[
              { key: 'all', label: t('blog.allLang') },
              { key: 'hi', label: t('blog.hindi') },
              { key: 'en', label: t('blog.english') },
            ].map(opt => (
              <button key={opt.key} onClick={() => setLangFilter(opt.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  langFilter === opt.key 
                    ? 'bg-gold/20 text-gold border border-gold/30' 
                    : 'bg-white/5 text-text-muted border border-white/10 hover:border-gold/20'
                }`}
                data-testid={`blog-filter-${opt.key}`}>
                {opt.label}
              </button>
            ))}
          </div>
          <div className="divine-divider mt-8 max-w-xs mx-auto" />
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="text-center text-text-muted" data-testid="blog-loading">Loading...</div>
          ) : fetchError ? (
            <div className="text-center py-10" data-testid="blog-fetch-error">
              <div className="text-text-muted mb-4">{fetchError}</div>
              <button
                onClick={fetchBlogs}
                className="px-6 py-2 rounded-full bg-gold/20 text-gold border border-gold/30 hover:bg-gold/30 transition-colors text-sm font-medium"
                data-testid="blog-fetch-retry"
              >
                {lang === 'hi' ? 'पुनः प्रयास करें' : 'Try again'}
              </button>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center text-text-muted" data-testid="blog-empty">{lang === 'hi' ? 'कोई ब्लॉग नहीं मिला' : 'No blogs found'}</div>
          ) : (
            <>
            {filteredBlogs.length === 0 ? (
              <div className="text-center text-text-muted py-8">{lang === 'hi' ? 'इस भाषा में कोई ब्लॉग नहीं मिला' : 'No blogs found in this language'}</div>
            ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <article 
                  key={blog.id} 
                  className="glass-card overflow-hidden group"
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={getBlogImage(blog, index)}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <span className="absolute bottom-4 left-4 px-3 py-1 bg-gold/90 text-background text-sm rounded-full flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {blog.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-text-muted mb-3">
                      <span className="flex items-center gap-2">
                        <img 
                          src="/media/founder/IMG_9620-removebg-preview.png" 
                          alt="Vivek Mudgal Astrologer" 
                          className="w-7 h-7 rounded-full object-cover object-top border border-gold/30"
                        />
                        <span>Vivek Mudgal Astrologer</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(blog.created_at)}
                      </span>
                    </div>

                    <h3 className="font-heading text-xl text-text-main mb-3 line-clamp-2 group-hover:text-gold transition-colors">
                      {getBlogTitle(blog)}
                      {translating[blog.id] && <span className="text-xs text-gold/60 ml-2 animate-pulse">translating...</span>}
                    </h3>
                    <p className="text-text-muted text-sm mb-4 line-clamp-3">
                      {getBlogExcerpt(blog)}
                    </p>

                    <Link 
                      to={`/blog/${blog.slug}`}
                      className="text-gold font-medium flex items-center gap-2 hover:gap-3 transition-all"
                      data-testid={`read-more-${blog.slug}`}
                    >
                      {t('blog.readMore')}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
            )}
            </>
          )}
        </div>
      </section>

      <RelatedTools />
      <Footer />
    </div>
  );
}
